import { Spin, message } from 'antd';
import React, { useState, useImperativeHandle, useCallback } from 'react';

import Icon from '@ant-design/icons';
import { useDeploy, useDeployCandidateList } from '@api/emp/queryHooks';
import deployLined from '@icons/emp/deploy.svg';
import { refetchMappedEventRelated, refetchMetricRelated } from '@src/emp-3.0/utils/refetch';
import Deployment from 'emp-3.0/components/DeploymentDrawer';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import { trackClickDeploy } from '@src/sensor';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import DeployStatus from './DeployStatus';

export interface DeployButtonRef {
  click: () => void;
}

interface DeployButtonProps {
  text?: string;
  style?: React.CSSProperties;
  metricType?: string;
  enableSecondPanel?: boolean;
}

const DeployButton = (
  { text = 'Deploy', style, metricType, enableSecondPanel = false }: DeployButtonProps,
  ref,
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeploymentDrawerOpen, setIsDeploymentDrawerOpen] = useState(false);
  const [deploySuccess, setDeploySuccess] = useState(false);
  const { mutate: deploy } = useDeploy();
  const { refetch: refetchDeployList } = useDeployCandidateList();
  const hasCreate = useCreatePermissions();
  const onOpenDeploymentDrawer = useCallback(() => {
    setIsDeploymentDrawerOpen(true);
    refetchDeployList();
  }, [refetchDeployList]);

  useImperativeHandle(
    ref,
    () => {
      return {
        click() {
          onOpenDeploymentDrawer();
        },
      };
    },
    [onOpenDeploymentDrawer],
  );

  const handleClickDeploy = (value) => {
    if (!hasCreate) {
      return;
    }
    setIsLoading(true);
    deploy(value, {
      onSuccess: () => {
        if (enableSecondPanel) {
          setDeploySuccess(true);
        } else {
          message.success('Deploy Success!');
          setIsDeploymentDrawerOpen(false);
        }
        refetchMappedEventRelated();
        refetchMetricRelated();
        trackClickDeploy({});
      },
      onError: () => {
        message.error('Failed to deploy, please try again later!');
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  const buttonIcon = isLoading ? (
    <Spin size="small" style={{ marginRight: 9 }} />
  ) : (
    <Icon component={deployLined} style={{ color: '#0046ff', marginRight: 4 }} />
  );

  return (
    <div style={{ display: 'inline-block' }}>
      <PermissionButton tooltip="" onClick={onOpenDeploymentDrawer} styleType="styled">
        <>
          {buttonIcon}
          {text}
          <DeployStatus />
        </>
      </PermissionButton>

      <Deployment
        loading={isLoading}
        enableSecondPanel={enableSecondPanel}
        deploySuccess={deploySuccess}
        metricType={metricType}
        drawerProps={{
          open: isDeploymentDrawerOpen,
          afterOpenChange: (open) => {
            if (!open) {
              setDeploySuccess(false);
            }
          },
          onClose: () => {
            setIsDeploymentDrawerOpen(false);
          },
        }}
        onDeploy={handleClickDeploy}
      />
    </div>
  );
};

export default React.forwardRef<DeployButtonRef, DeployButtonProps>(DeployButton);
